/* eslint-env browser */

/**
 * Show / hide control for password fields on the auth screens (FUIX-1514).
 *
 * Markup contract: a `button[data-password-toggle]` whose `aria-controls` names the
 * password input, with `data-label-show` / `data-label-hide` holding the translated
 * aria-labels. While the password is visible the button carries `data-revealed`,
 * which the icons key off to swap eye / eye-slash.
 */
export function initPasswordToggles(root = document) {
  root.querySelectorAll('[data-password-toggle]').forEach(button => {
    const input = document.getElementById(button.getAttribute('aria-controls'));
    if (!input) return;

    button.addEventListener('click', () => {
      const reveal = input.type === 'password';
      input.type = reveal ? 'text' : 'password';
      button.toggleAttribute('data-revealed', reveal);
      button.setAttribute(
        'aria-label',
        reveal ? button.dataset.labelHide : button.dataset.labelShow
      );
    });
  });
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Client-side checks for the sign-in form (FUIX-1589, PRD rule R2).
 *
 * Each field names its message element through `aria-describedby`; that element holds the
 * translated copy in `data-msg-required` / `data-msg-invalid`. An invalid field gets
 * `aria-invalid`, which the view styles as the error state.
 *
 * The inline script in coachs/sign_in.haml owns the submit request and is not changed. This
 * listener runs on `document` in the capture phase, so it sees the submit first and stops it
 * when a field is invalid: no request, no spinner.
 */
function fieldMessage(input) {
  const value = input.value.trim();
  const messages = document.getElementById(input.getAttribute('aria-describedby')).dataset;

  if (value === '') return messages.msgRequired;
  if (messages.msgInvalid && !EMAIL_PATTERN.test(value)) return messages.msgInvalid;
  return null;
}

function showFieldError(input, message) {
  const slot = document.getElementById(input.getAttribute('aria-describedby'));
  slot.textContent = message || '';
  slot.hidden = !message;
  if (message) {
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.removeAttribute('aria-invalid');
  }
}

export function initLoginValidation(formId = 'login-form') {
  const form = document.getElementById(formId);
  if (!form) return;

  const fields = [form.querySelector('#coach_email'), form.querySelector('#coach_password')];
  const email = fields[0];

  // Format is checked on blur only once something was typed, so tabbing past is quiet.
  email.addEventListener('blur', () => {
    if (email.value.trim() !== '') showFieldError(email, fieldMessage(email));
  });

  // A field in error clears as soon as its value becomes acceptable.
  fields.forEach(input => {
    input.addEventListener('input', () => {
      if (input.hasAttribute('aria-invalid')) showFieldError(input, fieldMessage(input));
    });
  });

  document.addEventListener(
    'submit',
    event => {
      if (event.target !== form) return;

      const invalid = fields.filter(input => {
        const message = fieldMessage(input);
        showFieldError(input, message);
        return message !== null;
      });
      if (invalid.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      // The submit button's onclick already showed the spinner.
      const spinner = document.getElementById('spinner');
      if (spinner) spinner.style.display = 'none';
      invalid[0].focus();
    },
    true
  );
}

/**
 * Busy state for the auth submit buttons (sign in, create account), as the DS Button `loading` prop.
 *
 * The inline scripts in coachs/sign_in.haml and onboarding/index.html.haml show and hide
 * `#spinner` and are not changed.
 * This observer follows the spinner and sets `aria-busy` on the button while the request runs.
 * The DS loading background is not applied yet: bg-interactive-primary-loading with
 * text-on-brand does not have sufficient contrast.
 * @ds-candidate fix: Button loading contrast — add the loading background when the DS token passes 4.5:1.
 */
export function initSubmitBusyState(formId = 'login-form') {
  const form = document.getElementById(formId);
  const spinner = document.getElementById('spinner');
  if (!form || !spinner) return;

  const button = form.querySelector('button[type="submit"]');
  const sync = () => {
    if (spinner.style.display !== 'none') {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  };

  new window.MutationObserver(sync).observe(spinner, {
    attributes: true,
    attributeFilter: ['style']
  });
}

/**
 * Password rules for the create-account and set-new-password forms (FUIX-1590, PRD rule R3).
 *
 * Markup contract: the password input names the rules container through `aria-describedby`.
 * Each `li[data-password-rule]` holds its regular expression; its `[data-rule-state]` child is
 * screen-reader text that flips between `data-label-met` / `data-label-unmet`.
 *
 * The rules are always visible, unchecked at rest, and check off live (`data-met` on the item).
 * They are never hidden, so the fields below do not move on focus or blur (FUIX-1592).
 * `markPasswordError()` sets `data-error` on the container after a failed submit, so unmet rules
 * show in the danger colour.
 * Returns an object with `isValid()` and `markPasswordError()`, or null when the markup is absent.
 */
export function initPasswordRules(input) {
  const container = input && document.getElementById(input.getAttribute('aria-describedby'));
  if (!container) return null;

  const rules = [...container.querySelectorAll('[data-password-rule]')].map(item => ({
    item,
    state: item.querySelector('[data-rule-state]'),
    pattern: new RegExp(item.dataset.passwordRule)
  }));

  const update = () => {
    rules.forEach(({ item, state, pattern }) => {
      const met = pattern.test(input.value);
      item.toggleAttribute('data-met', met);
      state.textContent = met ? state.dataset.labelMet : state.dataset.labelUnmet;
    });
    if (isValid()) {
      container.removeAttribute('data-error');
      input.removeAttribute('aria-invalid');
    }
  };

  const isValid = () => rules.every(({ pattern }) => pattern.test(input.value));

  input.addEventListener('input', update);

  update();

  return {
    isValid,
    markPasswordError() {
      container.setAttribute('data-error', '');
      input.setAttribute('aria-invalid', 'true');
    }
  };
}

/**
 * Client-side checks for the create-account form (FUIX-1590, PRD rules R1 to R3).
 *
 * Same contract as initLoginValidation: first name, last name and email name their message
 * element through `aria-describedby`. Email format is checked on blur once something was
 * typed; every field is checked on submit. The password is checked only on submit, through the
 * rules list.
 *
 * The submit button stays enabled. An invalid submit is stopped in the capture phase, before
 * the form posts: no request, no spinner, and focus moves to the first field to fix. The server
 * validation in CoachsController#create is unchanged and still has the final say.
 */
export function initSignupValidation(formId = 'signup-form') {
  const form = document.getElementById(formId);
  if (!form) return;

  const fields = ['#coach_firstname', '#coach_lastname', '#coach_email'].map(id =>
    form.querySelector(id)
  );
  const email = form.querySelector('#coach_email');
  const password = form.querySelector('#coach_password');
  const passwordRules = initPasswordRules(password);

  // Format is checked on blur only once something was typed, so tabbing past is quiet.
  email.addEventListener('blur', () => {
    if (email.value.trim() !== '') showFieldError(email, fieldMessage(email));
  });

  // A field in error clears as soon as its value becomes acceptable.
  fields.forEach(input => {
    input.addEventListener('input', () => {
      if (input.hasAttribute('aria-invalid')) showFieldError(input, fieldMessage(input));
    });
  });

  document.addEventListener(
    'submit',
    event => {
      if (event.target !== form) return;

      const invalid = fields.filter(input => {
        const message = fieldMessage(input);
        showFieldError(input, message);
        return message !== null;
      });
      if (passwordRules && !passwordRules.isValid()) {
        passwordRules.markPasswordError();
        invalid.push(password);
      }
      if (invalid.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      // The submit button's onclick already showed the spinner.
      const spinner = document.getElementById('spinner');
      if (spinner) spinner.style.display = 'none';
      invalid[0].focus();
    },
    true
  );
}

/**
 * Client-side check for the forgot-password form (FUIX-1591, PRD rule R2).
 *
 * Same contract as initLoginValidation, for the single email field. The form has no inline
 * script, so a valid submit shows the button spinner here and marks the button busy; the
 * page then posts and reloads as before. The server check in CoachsController#reset_password
 * is unchanged.
 */
export function initResetValidation(formId = 'reset-form') {
  const form = document.getElementById(formId);
  if (!form) return;

  const email = form.querySelector('#coach_account_email');

  // Format is checked on blur only once something was typed, so tabbing past is quiet.
  email.addEventListener('blur', () => {
    if (email.value.trim() !== '') showFieldError(email, fieldMessage(email));
  });

  // The field in error clears as soon as its value becomes acceptable.
  email.addEventListener('input', () => {
    if (email.hasAttribute('aria-invalid')) showFieldError(email, fieldMessage(email));
  });

  form.addEventListener('submit', event => {
    const message = fieldMessage(email);
    showFieldError(email, message);
    if (message) {
      event.preventDefault();
      email.focus();
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    const spinner = document.getElementById('spinner');
    if (spinner) spinner.style.display = 'block';
    if (button) button.setAttribute('aria-busy', 'true');
  });
}

/**
 * Set-new-password form (FUIX-1592, PRD rules R3 and R7), the page the reset email links to.
 *
 * The new password uses the same rules list as create account. The confirmation names its
 * message element through `aria-describedby`; that element holds the translated copy in
 * `data-msg-required` / `data-msg-mismatch`. Values are not trimmed: spaces are part of a
 * password.
 *
 * The submit button stays enabled. An invalid submit is stopped before the form posts, and
 * focus moves to the first field to fix. A valid submit shows the button spinner and marks the
 * button busy; the page then posts and reloads as before. The token and the password are still
 * checked by the API through CoachsController#edit_password, which is unchanged.
 */
export function initSetPasswordValidation(formId = 'edit-password-form') {
  const form = document.getElementById(formId);
  if (!form) return;

  const password = form.querySelector('#coach_password');
  const confirmation = form.querySelector('#coach_password_confirmation');
  const passwordRules = initPasswordRules(password);

  const confirmationMessage = () => {
    const messages = document.getElementById(confirmation.getAttribute('aria-describedby')).dataset;
    if (confirmation.value === '') return messages.msgRequired;
    if (confirmation.value !== password.value) return messages.msgMismatch;
    return null;
  };

  // The match is checked on blur only once something was typed, so tabbing past is quiet.
  confirmation.addEventListener('blur', () => {
    if (confirmation.value !== '') showFieldError(confirmation, confirmationMessage());
  });

  // A confirmation in error clears as soon as both fields match, from either field.
  [password, confirmation].forEach(input => {
    input.addEventListener('input', () => {
      if (confirmation.hasAttribute('aria-invalid')) {
        showFieldError(confirmation, confirmationMessage());
      }
    });
  });

  form.addEventListener('submit', event => {
    const invalid = [];
    if (passwordRules && !passwordRules.isValid()) {
      passwordRules.markPasswordError();
      invalid.push(password);
    }
    const message = confirmationMessage();
    showFieldError(confirmation, message);
    if (message) invalid.push(confirmation);

    if (invalid.length > 0) {
      event.preventDefault();
      invalid[0].focus();
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    const spinner = document.getElementById('spinner');
    if (spinner) spinner.style.display = 'block';
    if (button) button.setAttribute('aria-busy', 'true');
  });
}

/**
 * Language switcher below the card (FUIX-1593).
 *
 * Markup contract: a `details[data-language-switcher]` with a `summary` trigger and a `ul` menu
 * of links. <details> opens and closes the menu without JavaScript; this adds Escape (focus
 * returns to the trigger), click outside, and `data-placement="top"` when the menu does not fit
 * below the trigger, which the menu classes key off to open upward.
 */
export function initLanguageSwitcher(root = document) {
  root.querySelectorAll('details[data-language-switcher]').forEach(details => {
    const trigger = details.querySelector('summary');
    const menu = details.querySelector('ul');
    if (!trigger || !menu) return;

    const close = () => {
      details.open = false;
    };

    details.addEventListener('toggle', () => {
      if (!details.open) return;
      details.removeAttribute('data-placement');
      const menuHeight = menu.getBoundingClientRect().height;
      const triggerRect = trigger.getBoundingClientRect();
      const roomBelow = window.innerHeight - triggerRect.bottom;
      if (roomBelow < menuHeight + 16 && triggerRect.top > roomBelow) {
        details.setAttribute('data-placement', 'top');
      }
    });

    details.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !details.open) return;
      close();
      trigger.focus();
    });

    document.addEventListener('click', event => {
      if (details.open && !details.contains(event.target)) close();
    });
  });
}

if (typeof window !== 'undefined') {
  const init = () => {
    initLanguageSwitcher();
    initPasswordToggles();
    initLoginValidation();
    initSignupValidation();
    initResetValidation();
    initSetPasswordValidation();
    initSubmitBusyState();
    initSubmitBusyState('signup-form');
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
